import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { ToastContainer, toast } from "react-toastify";
import ClientContractService from "../../../services/ClientContractService";
import SocieteService from "../../../services/SocieteService";
import ClientContractPdf, { IWARE_VAT_RATE } from "../../../components/invoices/ClientContractPdf";
import OptionalSection from "../../../components/ui/OptionalSection";

const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// last day covered by a subscription starting on `startIso` (inclusive)
const computeEndDate = (startIso, periodicity) => {
  if (!startIso) return "";
  const [y, m, d] = startIso.split("-").map(Number);
  const end = new Date(y, m - 1, d);
  if (periodicity === "ANNUEL") end.setFullYear(end.getFullYear() + 1);
  else end.setMonth(end.getMonth() + 1);
  end.setDate(end.getDate() - 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
};

const STATUS_BADGE = {
  ACTIF: "badge-success",
  EXPIRE: "badge-danger",
  ANNULE: "badge-secondary",
};
const STATUS_LABEL = { ACTIF: "Actif", EXPIRE: "Expiré", ANNULE: "Remplacé" };

const ClientContract = () => {
  const navigate = useNavigate();
  const idSociete = new URLSearchParams(useLocation().search).get("idSociete");

  const [client, setClient] = useState({});
  const [contracts, setContracts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [amount, setAmount] = useState("");
  const [periodicity, setPeriodicity] = useState("MENSUEL");
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(computeEndDate(todayIso(), "MENSUEL"));
  // once the end date is edited by hand it is no longer recomputed
  const [endTouched, setEndTouched] = useState(false);

  // information printed in the contract only (not stored): the blank ones stay as lines to fill in by hand
  const [extras, setExtras] = useState({
    tvaRate: String(IWARE_VAT_RATE),
    paymentMode: "",
    paymentDelay: "",
    noticeDays: "",
    city: "",
    signatureDate: "",
    issuerRep: "",
    clientRep: "",
    clientLegalForm: "",
  });
  const setExtra = (field) => (e) => setExtras({ ...extras, [field]: e.target.value });
  const htAmount = Number(amount) || 0;
  const tvaAmount = (htAmount * (Number(extras.tvaRate) || 0)) / 100;

  const loadContracts = () =>
    ClientContractService.getBySociete(idSociete)
      .then((r) => setContracts(r.data?.success || []))
      .catch(() => toast.error("Erreur lors du chargement des contrats"));

  useEffect(() => {
    if (!idSociete) return;
    SocieteService.getSocieteById(idSociete)
      .then((r) => {
        const societe = typeof r.data?.Societe === "object" && r.data.Societe ? r.data.Societe : {};
        setClient(societe);
        // the representative and the legal form come from the company record; they stay editable
        setExtras((previous) => ({
          ...previous,
          clientRep: previous.clientRep || societe.managerName || "",
          clientLegalForm: previous.clientLegalForm || societe.legalForm || "",
        }));
      })
      .catch(() => toast.error("Erreur lors du chargement du client"));
    loadContracts();
  }, [idSociete]);

  const onPeriodicityChange = (value) => {
    setPeriodicity(value);
    if (!endTouched) setEndDate(computeEndDate(startDate, value));
  };
  const onStartChange = (value) => {
    setStartDate(value);
    if (!endTouched) setEndDate(computeEndDate(value, periodicity));
  };

  const downloadPdf = async (contract) => {
    try {
      const blob = await pdf(
        <ClientContractPdf contract={contract} client={client} extras={extras} />
      ).toBlob();
      saveAs(blob, `Contrat_${contract.ref || "abonnement"}.pdf`);
    } catch (e) {
      toast.error("Erreur lors de la génération du contrat");
    }
  };

  const onSave = () => {
    const clientErrors = {};
    if (!amount || Number(amount) <= 0) clientErrors.amount = "Le montant doit être supérieur à 0";
    if (!startDate) clientErrors.startDate = "Date d'activation obligatoire";
    if (!endDate) clientErrors.endDate = "Date de fin obligatoire";
    if (startDate && endDate && endDate < startDate)
      clientErrors.endDate = "La date de fin doit être postérieure à la date d'activation";
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    setSaving(true);
    ClientContractService.add({
      idSociete: Number(idSociete),
      amount: Number(amount),
      periodicity,
      startDate,
      endDate,
    })
      .then((r) => {
        if (r.data?.success) {
          toast.success("Contrat enregistré");
          loadContracts();
          downloadPdf(r.data.success);
        } else if (r.data?.error === "validation") {
          setErrors(r.data.fieldErrors || {});
        } else {
          toast.error("Erreur : " + (r.data?.error || "enregistrement impossible"));
        }
      })
      .catch(() => toast.error("Erreur : problème de connexion"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <ToastContainer />
      <section className="content-header">
        <div className="container-fluid">
          <h4 className="mb-1">
            <i className="fas fa-file-contract mr-2"></i>Contrat d'abonnement
          </h4>
          <small className="text-muted">
            Client : <b>{client.title || "-"}</b>
          </small>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          <div className="card card-dark">
            <div className="card-header">
              <h3 className="card-title">Nouveau contrat</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="form-group col-md-3">
                  <label>Montant HT de l'abonnement (TND)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    className={"form-control" + (errors.amount ? " is-invalid" : "")}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <div className="invalid-feedback">{errors.amount}</div>
                </div>
                <div className="form-group col-md-3">
                  <label>Durée de l'abonnement</label>
                  <select
                    className={"form-control" + (errors.periodicity ? " is-invalid" : "")}
                    value={periodicity}
                    onChange={(e) => onPeriodicityChange(e.target.value)}
                  >
                    <option value="MENSUEL">Mensuel</option>
                    <option value="ANNUEL">Annuel</option>
                  </select>
                  <div className="invalid-feedback">{errors.periodicity}</div>
                </div>
                <div className="form-group col-md-3">
                  <label>Date d'activation</label>
                  <input
                    type="date"
                    className={"form-control" + (errors.startDate ? " is-invalid" : "")}
                    value={startDate}
                    onChange={(e) => onStartChange(e.target.value)}
                  />
                  <div className="invalid-feedback">{errors.startDate}</div>
                </div>
                <div className="form-group col-md-3">
                  <label>Date de fin d'activation</label>
                  <input
                    type="date"
                    className={"form-control" + (errors.endDate ? " is-invalid" : "")}
                    value={endDate}
                    onChange={(e) => {
                      setEndTouched(true);
                      setEndDate(e.target.value);
                    }}
                  />
                  <div className="invalid-feedback">{errors.endDate}</div>
                </div>
              </div>
              <div className="mb-2 text-muted">
                TVA ({Number(extras.tvaRate) || 0} %) : <b>{tvaAmount.toFixed(3)} TND</b> - Montant TTC :{" "}
                <b>{(htAmount + tvaAmount).toFixed(3)} TND</b>
              </div>
              <OptionalSection title="Compléments imprimés dans le contrat">
                <div className="row">
                  <div className="form-group col-md-3">
                    <label>Taux de TVA (%)</label>
                    <input type="number" min="0" step="0.1" className="form-control" value={extras.tvaRate} onChange={setExtra("tvaRate")} />
                  </div>
                  <div className="form-group col-md-3">
                    <label>Préavis de résiliation (jours)</label>
                    <input type="number" min="0" className="form-control" value={extras.noticeDays} onChange={setExtra("noticeDays")} />
                  </div>
                  <div className="form-group col-md-3">
                    <label>Mode de paiement</label>
                    <input className="form-control" value={extras.paymentMode} onChange={setExtra("paymentMode")} placeholder="Ex : virement" />
                  </div>
                  <div className="form-group col-md-3">
                    <label>Délai de paiement</label>
                    <input className="form-control" value={extras.paymentDelay} onChange={setExtra("paymentDelay")} placeholder="Ex : 30 jours" />
                  </div>
                </div>
                <div className="row">
                  <div className="form-group col-md-4">
                    <label>Représentant du prestataire</label>
                    <input className="form-control" value={extras.issuerRep} onChange={setExtra("issuerRep")} />
                  </div>
                  <div className="form-group col-md-4">
                    <label>Représentant du client</label>
                    <input className="form-control" value={extras.clientRep} onChange={setExtra("clientRep")} />
                  </div>
                  <div className="form-group col-md-4">
                    <label>Forme juridique du client</label>
                    <input className="form-control" value={extras.clientLegalForm} onChange={setExtra("clientLegalForm")} placeholder="Ex : SARL" />
                  </div>
                </div>
                <div className="row">
                  <div className="form-group col-md-4">
                    <label>Fait à (ville)</label>
                    <input className="form-control" value={extras.city} onChange={setExtra("city")} />
                  </div>
                  <div className="form-group col-md-4">
                    <label>Date de signature</label>
                    <input type="date" className="form-control" value={extras.signatureDate} onChange={setExtra("signatureDate")} />
                  </div>
                </div>
                <small className="text-muted">
                  Les champs laissés vides apparaissent comme des lignes à remplir à la main sur le contrat.
                </small>
              </OptionalSection>
              <small className="text-muted">
                Un nouveau contrat remplace le contrat en cours. À la date de fin, le client est
                désactivé automatiquement (contrôle quotidien à 01h00).
              </small>
            </div>
            <div className="card-footer d-flex justify-content-between">
              <button className="btn btn-light" onClick={() => navigate("/adminSociete")}>
                Retour
              </button>
              <button className="btn btn-dark" onClick={onSave} disabled={saving}>
                <i className="fas fa-file-pdf mr-1"></i>
                Enregistrer et générer le contrat
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Historique des contrats</h3>
            </div>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-nowrap">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Montant</th>
                    <th>Durée</th>
                    <th>Activation</th>
                    <th>Fin</th>
                    <th>Statut</th>
                    <th className="text-center">Contrat</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        Aucun contrat : ce client est sans abonnement
                      </td>
                    </tr>
                  )}
                  {contracts.map((c) => (
                    <tr key={c.id}>
                      <td>{c.ref}</td>
                      <td>{Number(c.amount).toFixed(3)}</td>
                      <td>{c.periodicity === "ANNUEL" ? "Annuel" : "Mensuel"}</td>
                      <td>{c.startDate}</td>
                      <td>{c.endDate}</td>
                      <td>
                        <span className={"badge " + (STATUS_BADGE[c.status] || "badge-light")}>
                          {STATUS_LABEL[c.status] || c.status}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-dark btn-sm"
                          title="Télécharger le contrat"
                          onClick={() => downloadPdf(c)}
                        >
                          <i className="fas fa-file-pdf"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientContract;
